import React, { useState } from 'react';
import { Menu } from '@headlessui/react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  ChevronDownIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  QuestionMarkCircleIcon,
  GlobeAltIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { Search } from 'lucide-react';
import { FaUserNinja } from 'react-icons/fa6';
import { SiApple, SiGoogle } from 'react-icons/si';
import LanguageModal from './LanguageModal';
import { Link, useNavigate } from 'react-router-dom';
import { useAvatarCache } from '../hooks/useAvatarCache';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';
import ga from '../services/analytics';

interface CassetteMenuProps {
  user: { email: string; displayName?: string; photoURL?: string };
  handleLogout: () => void;
}

const CassetteMenu: React.FC<CassetteMenuProps> = ({ user, handleLogout }) => {
  const { t } = useTranslation();
  const { user: authUser, userType, applyPromoCode, isAnonymous, linkWithGoogle, linkWithApple } = useAuth();
  useTheme();
  const [languageOpen, setLanguageOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoMsg, setPromoMsg] = useState<string | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const navigate = useNavigate();

  const handleCopyProfileLink = () => {
    if (authUser?.uid) {
      const profileLink = `${window.location.origin}/u/${authUser.uid}`;
      navigator.clipboard.writeText(profileLink);
      toast.success(t('profile.profileLinkCopied'));
    }
  };
  
  // Use cached avatar to prevent excessive Google requests
  const cachedAvatarUrl = useAvatarCache(user.photoURL);

  const avatarIconSize = (size: string) => (size === 'w-14 h-14' ? 'w-7 h-7' : 'w-4 h-4');

  // Create a reusable avatar component to prevent duplicate requests
  const AvatarDisplay = ({ size = 'w-8 h-8' }: { size?: string }) => {
    if (isAnonymous) {
      return (
        <div
          className={`${size} rounded-full flex items-center justify-center shadow-sm`}
          style={{ backgroundColor: 'var(--color-hover)' }}
          aria-label={t('auth.guest.avatarLabel')}
        >
          <FaUserNinja className={avatarIconSize(size)} style={{ color: 'var(--brand-from)' }} aria-hidden />
        </div>
      );
    }

    return (
      <div className={`${size} rounded-full gradient-brand-br flex items-center justify-center shadow-sm`}>
        {cachedAvatarUrl ? (
          <img
            src={cachedAvatarUrl}
            alt="User avatar"
            className={`${size} rounded-full object-cover`}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const nextElement = e.currentTarget.nextElementSibling;
              if (nextElement && nextElement.classList) {
                nextElement.classList.remove('hidden');
              }
            }}
          />
        ) : null}
        <span className={`text-sm font-semibold text-white ${cachedAvatarUrl ? 'hidden' : ''}`}>
          {user.email.charAt(0).toUpperCase()}
        </span>
      </div>
    );
  };

  return (
    <>
      <Menu as="div" className="relative inline-block text-left z-50">
        <Menu.Button className="flex items-center space-x-2 p-2 rounded-lg hover:bg-[var(--color-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--btn-primary-ring)] transition-colors">
          <AvatarDisplay />
          <ChevronDownIcon className="w-4 h-4" style={{ color: 'var(--color-muted)' }} />
        </Menu.Button>
        <Menu.Items className="absolute right-0 mt-2 w-80 origin-top-right z-[60] bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-lg focus:outline-none py-2 max-h-[85vh] overflow-y-auto">
          {/* User info - centered but compact */}
          <div className="px-4 pb-2">
            {/* Avatar and Name Section - Centered */}
            <div className="flex flex-col items-center text-center mb-3">
              <div className="mb-2">
                <AvatarDisplay size="w-14 h-14" />
              </div>
              <h3 className="font-semibold text-base" style={{ color: 'var(--color-text)' }}>
                {user.displayName || user.email.split('@')[0]}
              </h3>
              <p className="text-sm break-all" style={{ color: 'var(--color-muted)' }}>{user.email}</p>
              <span className={`mt-2 px-3 py-0.5 rounded-full text-xs font-semibold ${
                isAnonymous
                  ? 'bg-[var(--color-hover)] text-[var(--color-muted)]'
                  : userType === 'artist' || userType === 'studio'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-600'
              }`} style={!isAnonymous && (userType === 'artist' || userType === 'studio') ? {
                backgroundColor: userType === 'studio' ? '#9333ea' : 'var(--brand-from)',
              } : undefined}>
                {isAnonymous
                  ? t('auth.guest.badge')
                  : userType === 'artist'
                  ? t('menu.artist')
                  : userType === 'studio'
                  ? t('menu.studio')
                  : t('menu.starter')}
              </span>
            </div>
          </div>
          <div className="border-t my-1" style={{ borderColor: 'var(--color-border)' }} />
          {/* Actions */}
          <div className="flex flex-col px-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={handleCopyProfileLink}
                  className={`flex items-center w-full px-4 py-2 text-sm rounded-lg transition-colors ${active ? 'bg-[var(--color-hover)]' : ''}`}
                >
                  <DocumentDuplicateIcon className="w-5 h-5 mr-3" style={{ color: 'var(--color-muted)' }} />
                  <span style={{ color: 'var(--color-text)' }}>{t('menu.copyShareLink')}</span>
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/settings"
                  className={`flex items-center w-full px-4 py-2 text-sm rounded-lg transition-colors ${active ? 'bg-[var(--color-hover)]' : ''}`}
                >
                  <Cog6ToothIcon className="w-5 h-5 mr-3" style={{ color: 'var(--color-muted)' }} />
                  <span style={{ color: 'var(--color-text)' }}>{t('menu.settings')}</span>
                </Link>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/help"
                  className={`flex items-center w-full px-4 py-2 text-sm rounded-lg transition-colors ${active ? 'bg-[var(--color-hover)]' : ''}`}
                  onClick={() => ga.event('help_page_viewed')}
                >
                  <QuestionMarkCircleIcon className="w-5 h-5 mr-3" style={{ color: 'var(--color-muted)' }} />
                  <span style={{ color: 'var(--color-text)' }}>{t('menu.help')}</span>
                </Link>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => setLanguageOpen(true)}
                  className={`flex items-center w-full px-4 py-2 text-sm rounded-lg transition-colors ${active ? 'bg-[var(--color-hover)]' : ''}`}
                >
                  <GlobeAltIcon className="w-5 h-5 mr-3" style={{ color: 'var(--color-muted)' }} />
                  <span style={{ color: 'var(--color-text)' }}>{t('menu.language')}</span>
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/upgrade"
                  className={`flex items-center w-full px-4 py-2 text-sm rounded-lg transition-colors ${active ? 'bg-[var(--color-hover)]' : ''}`}
                >
                  <Search className="w-5 h-5 mr-3" style={{ color: 'var(--color-muted)' }} />
                  <span style={{ color: 'var(--color-text)' }}>{t('menu.comparePlans')}</span>
                </Link>
              )}
            </Menu.Item>
          </div>
          
          {/* Subscribe - Only for starter users */}
          {userType === 'starter' && (
            <div className="px-4 pt-2">
              <button
                className="w-full py-2 text-sm rounded-lg btn-brand text-white font-semibold text-center transition shadow"
                onClick={() => navigate('/upgrade')}
              >
                {t('menu.subscribe')}
              </button>
              <button
                className="w-full text-xs hover:underline py-1"
                style={{ color: 'var(--info-blue-text)' }}
                onClick={() => { setPromoOpen(true); setPromoMsg(null); setPromoInput(''); }}
              >
                {t('menu.havePromoCode')}
              </button>
            </div>
          )}
          
          <div className="border-t my-1" style={{ borderColor: 'var(--color-border)' }} />
          {isAnonymous && (
            <div className="px-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => void linkWithGoogle()}
                    className={`flex items-center w-full px-4 py-2 text-sm rounded-lg transition-colors ${active ? 'bg-[var(--color-hover)]' : ''}`}
                    data-testid="menu-link-google"
                  >
                    <SiGoogle className="w-5 h-5 mr-3" style={{ color: 'var(--brand-from)' }} />
                    <span style={{ color: 'var(--color-text)' }}>{t('auth.continueWithGoogle')}</span>
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => void linkWithApple()}
                    className={`flex items-center w-full px-4 py-2 text-sm rounded-lg transition-colors ${active ? 'bg-[var(--color-hover)]' : ''}`}
                    data-testid="menu-link-apple"
                  >
                    <SiApple className="w-5 h-5 mr-3" style={{ color: 'var(--color-text)' }} />
                    <span style={{ color: 'var(--color-text)' }}>{t('auth.continueWithApple')}</span>
                  </button>
                )}
              </Menu.Item>
            </div>
          )}
          {isAnonymous && <div className="border-t my-1" style={{ borderColor: 'var(--color-border)' }} />}
          <div className="px-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={handleLogout}
                  className={`flex items-center w-full px-4 py-2 text-sm rounded-lg transition-colors ${active ? 'bg-[var(--color-hover)]' : ''}`}
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3 text-red-400" />
                  <span className="text-red-600">{t('menu.logOut')}</span>
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Menu>
      
      {/* Promo code modal */}
      {promoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="rounded-xl shadow-lg p-6 w-full max-w-xs flex flex-col items-center border" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--color-text)' }}>
              {t('menu.promoModal.title')}
            </h3>
            <input
              type="text"
              value={promoInput}
              onChange={e => setPromoInput(e.target.value)}
              className="border rounded px-3 py-2 w-full mb-2 bg-transparent"
              style={{ borderColor: 'var(--color-border)', color: 'var(--color-text)' }}
              placeholder={t('menu.promoModal.placeholder')}
              disabled={promoLoading}
            />
            {promoMsg && (
              <div className={`mb-2 text-sm ${promoMsg.startsWith('Promo code applied') ? 'text-green-600' : 'text-red-600'}`}>{promoMsg}</div>
            )}
            <div className="flex gap-2 w-full">
              <button
                className="flex-1 px-3 py-2 rounded text-white font-semibold transition" style={{ backgroundColor: 'var(--brand-from)' }}
                disabled={promoLoading}
                onClick={async () => {
                  setPromoLoading(true);
                  const res = await applyPromoCode(promoInput.trim());
                  setPromoMsg(res.message);
                  setPromoLoading(false);
                  if (res.success) {
                    setTimeout(() => setPromoOpen(false), 1200);
                  }
                }}
              >
                {t('menu.promoModal.apply')}
              </button>
              <button
                className="flex-1 px-3 py-2 rounded font-semibold transition"
                style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }}
                onClick={() => setPromoOpen(false)}
                disabled={promoLoading}
              >
                {t('menu.promoModal.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <LanguageModal open={languageOpen} onClose={() => setLanguageOpen(false)} />
    </>
  );
};

export default CassetteMenu; 